import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

// Context
import { ACTIONS, useModalHistory } from "../../context/ModalHistoryProvider";

// API
import auth from "../../auth/auth";

// Components
import PageStyle from "../../components/Styled/Page.style";
import ForgotPasswordModal from "../../components/Modals/ForgotPassword/ForgotPasswordModal";
import { EmailInput } from "../../components/Controls/EmailInput";
import { LoginButton } from "../../components/Controls/LoginButton";
import { PasswordInput } from "../../components/Controls/PasswordInput";
import { RememberMeCheckbox } from "../../components/Controls/RememberMeCheckbox";

// Utils
import colors from "../../utils/constants/COLORS";
import typography from "../../utils/constants/TYPOGRAPHY";
import {
  REMEMBER_ME_KEY,
  TOKEN_KEY,
  USER_KEY,
} from "../../utils/constants/SYSTEM";

// Assets
import logo from "../../assets/images/heycarter-logo.png";
import { useNavigate } from "react-router";
import { set } from "../../utils/helpers/storage";
import inputValidators from "../../utils/helpers/inputValidators";
import useToast, { TOAST_TYPES } from "../../utils/hooks/useToast";

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [{ modalHistory }, dispatchModalHistory] = useModalHistory();
  const pushEnd = (modal) =>
    dispatchModalHistory({ type: ACTIONS.PUSH_END, payload: { value: modal } });
  const currentModal = useMemo(() => {
    return modalHistory[modalHistory.length - 1];
  }, [modalHistory]);
  const [addToast] = useToast();
  const navigate = useNavigate();

  const { email: emailValidator } = inputValidators;

  useEffect(() => {
    localStorage.setItem(REMEMBER_ME_KEY, `${rememberMe}`);
  }, [rememberMe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    const res = await auth.loginAsync(email, password);
    if (res) {
      const { jwt, user } = res;
      set(TOKEN_KEY, jwt); // Remove if Verify token working
      set(USER_KEY, user); // Remove if Verify token working
      //pushEnd(<VerifyTokenModal jwt={jwt} user={user} />);  // Uncomment if Verify token working
      navigate("/"); // Remove if Verify token working
    } else {
      addToast(TOAST_TYPES.ERROR, "Incorrect username or password.");
    }

    setIsLoading(false);
  };

  return (
    <PageStyle>
      <Heading>
        Deal Portal <img src={logo} alt="HeyCarter logo" height="20px" />
      </Heading>
      <Inner>
        <form onSubmit={handleSubmit} autoComplete="off">
          <EmailInput
            id="email-address-login"
            value={email}
            setValue={setEmail}
            isLoading={isLoading}
            error={() => emailValidator(email)}
            labelTop
          />
          <br />
          <PasswordInput
            value={password}
            setValue={setPassword}
            isLoading={isLoading}
            labelTop
          />
          <br />
          <Actions>
            <RememberMeCheckbox
              onClick={() => setRememberMe((prev) => !prev)}
              checked={rememberMe}
            />
            <ForgotPasswordButton
              onClick={() =>
                pushEnd(<ForgotPasswordModal inputValue={email} />)
              }
            >
              Forgot Password?
            </ForgotPasswordButton>
          </Actions>
          <LoginButton isLoading={isLoading} />
        </form>
      </Inner>
      {currentModal}
    </PageStyle>
  );
}

const Heading = styled.h3`
  color: #ffffff;
  width: 22rem;
  margin-bottom: 0;
  padding: 1rem;
  /* border-bottom: 1px dashed #333; */
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background-color: #008060;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 0.5rem;
  padding: 0 0.25rem;
`;

const ForgotPasswordButton = styled.div`
  color: ${colors.tertiary};
  cursor: pointer;

  font-size: ${typography.button.size};
  font-weight: ${typography.button.weight};
  line-height: ${typography.button.lineHeight};

  height: 100%;

  &:hover {
    color: ${colors.secondary};
  }

  &:active {
    color: ${colors.tertiary};
  }

  user-select: none;
`;

const Inner = styled.div`
  color: ${colors.primary};
  width: 22rem;
  padding: 1rem;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  background-color: #fff;
`;
